import React from 'react';
import { useTranslation } from 'react-i18next';
import { CellTypes, TableDefinition } from '~/Components/DataTable/DataTable.types';
import { CHARCOAL_GREY, LIGHT_BLUE } from '~/Constants/Colors.constants';
import { FONT_SIZE_MEDIUM } from '~/Constants/UI.constants';
import { UserRole } from '~/Models/DTO/Role.model';
import { IOrgRoles } from '~/Models/interfaces/OrgStaff.model';
import { Avatar } from '~/UIKit';
import { makeStyles } from '~/UIKit/Style';

const useStyles = makeStyles(
  () => ({
    wrapper: {
      display: 'flex',
      alignItems: 'center',
    },
    text: {
      marginLeft: 15,
    },
    contactWrapper: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
    },
    iconWrapper: {
      backgroundColor: LIGHT_BLUE,
      borderRadius: 30,
      minWidth: 25,
      maxWidth: 25,
      minHeight: 25,
      maxHeight: 25,
      fontSize: FONT_SIZE_MEDIUM,
      display: 'flex',
      alignItems: 'center',
      placeContent: 'center',
      marginRight: 15,
      color: CHARCOAL_GREY,
      '& > svg': {
        fontSize: 'inherit',
      },
    },
    contactText: {
      marginLeft: 5,
    },
  }),
  { name: 'orgStaffListTable' },
);

export const useOrgStaffListDesktopTableDefinition = (): TableDefinition => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const getNameOfRole = (type: UserRole) => {
    switch (type) {
      case UserRole.FULL_ACCESS:
        return t('access.role.manager');
      case UserRole.TRAINER:
        return t('access.role.trainer');
      case UserRole.RESTRICTED:
        return t('access.role.restricted');
      default:
        return '';
    }
  };

  const displayRoles = (roles: IOrgRoles[]) => {
    const organisationRoles = roles.filter(
      (role) => role.resources.filter((r) => r.resourceType === 'ORGANISATION').length > 0,
    );

    if (organisationRoles.length > 0) {
      return <div>{t('access.role.organisationManager')}</div>;
    }

    const providerRoles = roles.filter(
      (role) => role.resources.filter((r) => r.resourceType === 'PROVIDER').length > 0,
    );

    return providerRoles.map((role) => {
      const providerNames = role.resources
        .filter((resource) => resource.resourceType === 'PROVIDER')
        .map((resource) => resource.name);

      let text = `${getNameOfRole(role.accessType)} - `;

      if (providerNames.length < 1) {
        text = '';
      } else {
        text =
          providerNames.length === 1
            ? text.concat(providerNames[0] || ' ')
            : text.concat(
                `${
                  providerNames.length < 4
                    ? providerNames.join(', ')
                    : `${providerNames.slice(0, 3).join(', ')}...`
                }`,
              );
      }

      return <div key={`access_provider_${role.accessType}`}>{text}</div>;
    });
  };

  return {
    config: { useSelectableRows: true },
    columns: [
      {
        label: t('table.column.staffMember'),
        propertyIds: ['imageUrl', 'firstName', 'lastName'],
        formatter: (values) => (
          <div className={classes.wrapper}>
            <Avatar imageUrl={values.imageUrl} />
            <div className={classes.text}>{`${values.firstName} ${values.lastName}`}</div>
          </div>
        ),
        cellType: CellTypes.String,
      },
      {
        label: t('table.column.contact'),
        propertyIds: ['email', 'phone'],
        cellType: CellTypes.String,
        formatter: (values) => (
          <div>
            {values.phone && (
              <div className={classes.contactWrapper}>
                <div className={classes.contactText}>{`${values.phone}`}</div>
              </div>
            )}
            {values.email && (
              <div className={classes.contactWrapper}>
                <div className={classes.contactText}>{`${values.email}`}</div>
              </div>
            )}
          </div>
        ),
      },
      {
        label: t('table.column.access'),
        propertyIds: 'roles',
        formatter: (roles: IOrgRoles[]) => <div>{displayRoles(roles)}</div>,
        cellType: CellTypes.String,
      },
    ],
  };
};
